<template>
  <div
    class="sign-up-modal"
    v-loading="isLoading"
  >
    <app-i18n
      class="sign-up-modal__title"
      code="auth.signup"
    />
    <div class="sign-up-modal__title-separator" />

    <el-form
      v-if="model"
      label-position="top"
      class="sign-up-modal__form"
      :rules="rules"
      :model="model"
      :validate-on-rule-change="false"
      ref="form"
      @validate="onFormValidate"
      @submit.prevent.native="$event => activeStepIndex === 0 ? doNext($event) : doSubmit($event)"
      @keypress.enter.native="$event => activeStepIndex === 0 ? doNext($event) : doSubmit($event)"
    >
      <el-steps
        :active="activeStepIndex"
        align-center
        finish-status="success"
      >
        <el-step :title="i18n('user.form.subtitle.parentInfo')" />
        <el-step :title="i18n('user.form.subtitle.parentAddress')" />
      </el-steps>

      <el-row
        v-show="activeStepIndex === 0"
        class="sign-up-modal__form__step sign-up-modal__form__step--first"
        :gutter="10"
      >
        <!-- First Name -->
        <el-col :lg="7" :md="7" :sm="7">
          <el-form-item
            :label="fields.firstName.label"
            :prop="fields.firstName.name"
            :required="fields.firstName.required"
          >
            <el-input
              v-model="model[fields.firstName.name]"
              :placeholder="i18n('user.placeholders.firstName')"
              prefix-icon="icon-player"
            />
          </el-form-item>
        </el-col>

        <!-- Last Name -->
        <el-col :lg="11" :md="11" :sm="11">
          <el-form-item
            :label="fields.lastName.label"
            :prop="fields.lastName.name"
            :required="fields.lastName.required"
          >
            <el-input
              v-model="model[fields.lastName.name]"
              :placeholder="i18n('user.placeholders.lastName')"
              prefix-icon="icon-player"
            />
          </el-form-item>
        </el-col>

        <!-- Gender -->
        <el-col :lg="6" :md="6" :sm="6">
          <el-form-item
            :label="fields.gender.label"
            :prop="fields.gender.name"
            :required="fields.gender.required"
          >
            <el-select
              :placeholder="i18n('user.placeholders.gender')"
              v-model="model[fields.gender.name]"
            >
              <i
                slot="prefix"
                class="icon-family"
              />
              <el-option
                :key="option.id"
                :label="option.label"
                :value="option.id"
                v-for="option in fields.gender.options"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <!-- Birthdate -->
        <el-col :lg="7" :md="7" :sm="7">
          <el-form-item
            :label="fields.birthdate.label"
            :prop="fields.birthdate.name"
            :required="fields.birthdate.required"
          >
            <el-date-picker
              type="date"
              :placeholder="i18n('common.date.formatInput')"
              :format="i18n('common.date.formatInput')"
              v-model="model[fields.birthdate.name]">
            </el-date-picker>
          </el-form-item>
        </el-col>

        <!-- Phone Number -->
        <el-col :lg="17" :md="17" :sm="17">
          <el-form-item
            :label="fields.phoneNumber.label"
            :prop="fields.phoneNumber.name"
            :required="fields.phoneNumber.required"
          >
            <app-phone-input v-model="model[fields.phoneNumber.name]" />
          </el-form-item>
        </el-col>

        <!-- Email -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            :label="fields.email.label"
            :prop="fields.email.name"
            :required="fields.email.required"
          >
            <el-input
              v-model="model[fields.email.name]"
              :placeholder="i18n('user.placeholders.email')"
              prefix-icon="icon-mail"
            />
          </el-form-item>
        </el-col>

        <!-- Password -->
        <el-col :lg="12" :md="12" :sm="12">
          <el-form-item
            :label="fields.password.label"
            :prop="fields.password.name"
            :required="fields.password.required"
          >
            <el-input
              v-model="model[fields.password.name]"
              class="el-input--password"
              type="password"
              show-password
              :placeholder="i18n('user.placeholders.password')"
              prefix-icon="icon-lock"
            />
          </el-form-item>
        </el-col>

        <!-- Confirm Password -->
        <el-col :lg="12" :md="12" :sm="12">
          <el-form-item
            :label="fields.confirmPassword.label"
            :prop="fields.confirmPassword.name"
            :required="fields.confirmPassword.required"
            :password="model[fields.password.name]"
          >
            <el-input
              v-model="model[fields.confirmPassword.name]"
              class="el-input--password"
              type="password"
              show-password
              :placeholder="i18n('user.placeholders.confirmPassword')"
              prefix-icon="icon-lock"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row
        v-show="activeStepIndex === 1"
        class="sign-up-modal__form__step sign-up-modal__form__step--second"
        :gutter="10"
      >
        <!-- Address -->
        <el-col :lg="24" :md="24" :sm="24">
          <app-address-form-partial
            ref="addressForm"
            :field-name="fields.address.name"
            :address.sync="model[fields.address.name]"
          />
        </el-col>

        <!-- Terms and Conditions -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            class="sign-up-modal__form__terms-and-conditions"
            :prop="fields.tosAccepted.name"
            :required="fields.tosAccepted.required"
          >
            <el-checkbox v-model="model[fields.tosAccepted.name]">
              <span v-html="fields.tosAccepted.label" />
            </el-checkbox>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row
        class="sign-up-modal__form__step"
        :gutter="10"
      >
          <!-- Global error message -->
          <el-col :lg="24" :md="24" :sm="24">
            <el-form-item
              class="sign-up-modal__form__global-error"
              v-if="globalErrorMessage"
              :error="globalErrorMessage"
            />
          </el-col>
      </el-row>

      <el-form-item class="sign-up-modal__form__controls">
        <!-- Next -->
        <el-button
          v-if="activeStepIndex === 0"
          type="primary"
          @click="doNext"
          :disabled="isLoading"
        >
          <app-i18n code="common.next" />
        </el-button>

        <!-- Back button -->
        <el-button
          v-if="activeStepIndex === 1"
          :disabled="isLoading"
          @click="doBack"
          type="secondary"
        >
          <app-i18n code="common.backButton" />
        </el-button>

        <!-- Sign up -->
        <el-button
          v-if="activeStepIndex === 1"
          type="primary"
          @click="doSubmit"
          :disabled="isLoading"
        >
          <app-i18n code="auth.signup" />
        </el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'

import { signup } from '@/auth'

import { i18n } from '@shared/i18n'
import { FormSchema } from '@shared/form/form-schema'

import { UserModel } from '@modules/auth/user-model'

export default {
  name: 'sign-up--modal',
  data() {
    return {
      activeStepIndex: 0,
      model: {},
      globalErrorMessage: null,
      isLoading: false,
      i18n,
      siteFetchPromise: new Promise(resolve => resolve()),
      siteFetchPromiseResolve: () => {},
    }
  },
  props: {
    siteLoading: Boolean,
  },
  computed: {
    ...mapGetters({
      applyFlowSuccessCallback: 'applyFlowSuccessCallback',
      title: 'title'
    }),
    slug() {
      return this.$route.params.slug
    },
    rules() {
      const { formSchema, fields } = this;
      const rules = formSchema.rules()

      const stepOneRules = {
        ...formSchema.rules(),
        [fields.confirmPassword.name]: [
          ...formSchema.rules()[fields.confirmPassword.name],
          {
            validator: (rule, value, callback) => {
              const errorMessage = i18n('user.fields.confirmPassword.validation.notEqual')

              const password = this.model[fields.password.name]
              const confirmPassword = this.model[fields.confirmPassword.name]
              const doPasswordsMatch = password === confirmPassword

              if (!doPasswordsMatch) callback(new Error(errorMessage))
              else callback()
            },
            trigger: 'blur',
          }
        ]
      }
      delete stepOneRules[fields.address.name]
      delete stepOneRules[fields.tosAccepted.name]

      const stepTwoRules = {
        [fields.address.name]: rules[fields.address.name],
        [fields.tosAccepted.name]: rules[fields.tosAccepted.name],
      }

      if (this.activeStepIndex === 0) return stepOneRules
      return stepTwoRules
    },
    
    formSchema() {
      const { fields } = this;
      return new FormSchema([
        fields.firstName,
        fields.lastName,
        fields.gender,
        fields.birthdate,
        fields.phoneNumber,
        fields.email,
        fields.address,
        fields.password,
        fields.confirmPassword,
        fields.tosAccepted,
      ]);
    },

    fields() {
      return new UserModel().fields();
    },
  },
  mounted () {
    window.addEventListener('touchstart', ev => {
      if (ev.target.classList.contains('el-select-dropdown__item')) event.stopPropagation();
    }, true);
  },
  methods: {
    ...mapMutations({
      setSignupModalVisible: 'setSignupModalVisible',
      setBetweenStepsModalVisible: 'setBetweenStepsModalVisible',
      setApplyFlowSuccessCallback: 'setApplyFlowSuccessCallback',
      setLockDialog: 'setLockDialog',
    }),
    doBack() {
      this.activeStepIndex--
    },
    async doNext() {
      try {
        await Promise.all([
          this.$refs.form.validate(),
        ])
      } catch (e) {
        return
      }
      this.activeStepIndex++
    },
    async doSubmit() {
      const { formSchema } = this;
      
      try {
        await Promise.all([
          this.$refs.form.validate(),
          this.$refs.addressForm.validate(),
        ])
      } catch (e) {
        return
      }

      try {
        this.isLoading = true
        const data = formSchema.cast(this.model)
        await signup(data, this.slug, this.title)
        this.onSuccess()
        await this.onClose()
        this.setBetweenStepsModalVisible(true)
      } catch (e) {
        const { code } = e
        if (code) this.globalErrorMessage = i18n(`firebaseErrors.${code}`)
        else this.globalErrorMessage = i18n('common.unknownError')
      } finally {
        this.isLoading = false
      }
    },
    onSuccess() {
      if (typeof this.applyFlowSuccessCallback !== 'function') return
      this.applyFlowSuccessCallback()
      this.setApplyFlowSuccessCallback(null)
    },
    async onClose() {
      await this.$nextTick()
      await this.siteFetchPromise
      this.setSignupModalVisible(false)
    },
    onLoadingChange(isLoading) {
      this.setLockDialog(isLoading)
    },
    onFormValidate() {
      this.globalErrorMessage = null
    },
  },
  watch: {
    isLoading: {
      handler: 'onLoadingChange',
      immediate: true,
    },
    siteLoading(val) {
      if (val) {
        this.siteFetchPromise = new Promise(resolve => this.siteFetchPromiseResolve = resolve)
      } else {
        this.siteFetchPromiseResolve()
        this.siteFetchPromiseResolve = () => {}
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.sign-up-modal {
  @include modal-mixin;

  .el-steps {
    padding: 0 0 32px;

    .el-step {
      @include mobile {
        font-size: 12px;
      }
    }

  }

  &__form {
    box-sizing: border-box;
    padding-bottom: 0;
    padding-top: 24px;

    &__step {
      padding: 0 64px;

      @include mobile {
        padding: 0 16px;
      }

      .el-row {
        margin-left: 0;
      }

    }

    &__controls {
      display: grid;
      align-content: center;
      justify-content: center;
      height: 120px;
      background-color: #f8f8f8;
    }

    &__terms-and-conditions {
      display: grid;
      align-content: center;
      justify-content: start;
      .el-checkbox {
        @include mobile {
          white-space: normal;
          line-height: normal;
        }

      }
    }
  }
}
</style>
<style lang="scss">
.sign-up-modal {
  .el-step__title {
    @include mobile {
      padding-top: 8px;
      line-height: 20px;
    }
  }
  .el-form-item__error {
    @include mobile {
        position: absolute;
    }
  }
}
</style>